import { Controller } from '@hotwired/stimulus'
import { Chart, registerables } from 'chart.js'

Chart.register(...registerables)

/**
 * MultiChartController - manages multiple Chart.js instances and syncs dataset visibility
 *
 * Usage:
 *   <div data-controller="multi-chart">
 *     <canvas data-multi-chart-target="canvas" data-chart-type="line" data-chart-data="..." data-chart-options="..."></canvas>
 *     <canvas data-multi-chart-target="canvas" data-chart-type="line" data-chart-data="..." data-chart-options="..."></canvas>
 *     <input type="checkbox" data-action="change->multi-chart#toggleDataset" data-dataset-indices="0,1" checked>
 *   </div>
 */
export default class extends Controller {
  static targets = ['canvas']

  connect() {
    this.charts = []
    this.initializeCharts()
  }

  disconnect() {
    this.destroyCharts()
  }

  initializeCharts() {
    this.destroyCharts()

    this.canvasTargets.forEach((canvas) => {
      const type = canvas.dataset.chartType || 'line'
      const data = JSON.parse(canvas.dataset.chartData || '{}')
      const options = JSON.parse(canvas.dataset.chartOptions || '{}')

      const chart = new Chart(canvas, { type, data, options })
      this.charts.push(chart)
    })
  }

  destroyCharts() {
    this.charts.forEach((chart) => chart.destroy())
    this.charts = []
  }

  toggleDataset(event) {
    if (this.charts.length === 0) return

    const indicesRaw = event.target.dataset.datasetIndices
    const indices = indicesRaw
      ? indicesRaw
          .split(',')
          .map((s) => parseInt(s.trim(), 10))
          .filter((n) => !Number.isNaN(n))
      : [parseInt(event.target.dataset.datasetIndex, 10)]

    const isVisible = event.target.checked

    // Toggle visibility on ALL charts
    this.charts.forEach((chart) => {
      indices.forEach((index) => {
        if (index < chart.data.datasets.length) {
          chart.setDatasetVisibility(index, isVisible)
        }
      })
      chart.update()
    })

    this.updateSelectAllCheckbox()
  }

  toggleAllDatasets(event) {
    if (this.charts.length === 0) return

    const isVisible = event.target.checked

    // Toggle all datasets on ALL charts
    this.charts.forEach((chart) => {
      const allIndices = chart.data.datasets.map((_, index) => index)
      allIndices.forEach((index) => {
        chart.setDatasetVisibility(index, isVisible)
      })
      chart.update()
    })

    // Update all individual checkboxes
    const checkboxes = this.element.querySelectorAll('.dataset-checkbox')
    checkboxes.forEach((checkbox) => {
      checkbox.checked = isVisible
    })
  }

  updateSelectAllCheckbox() {
    const checkboxes = this.element.querySelectorAll('.dataset-checkbox')
    const selectAllCheckbox = this.element.querySelector('#toggle-all-datasets')

    if (!selectAllCheckbox || checkboxes.length === 0) return

    const checkedCount = Array.from(checkboxes).filter((cb) => cb.checked).length

    if (checkedCount === 0) {
      selectAllCheckbox.checked = false
      selectAllCheckbox.indeterminate = false
    } else if (checkedCount === checkboxes.length) {
      selectAllCheckbox.checked = true
      selectAllCheckbox.indeterminate = false
    } else {
      selectAllCheckbox.checked = false
      selectAllCheckbox.indeterminate = true
    }
  }
}
